import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getItemsServices, createItemService, updateItemService, deleteItemService } from '../services/api';
import { Plus, Search, Package, X, Loader2, Edit, Trash2 } from 'lucide-react';

const itemTypes = { 1: 'Materials', 2: 'MISC', 3: 'Labour', 4: 'Other' };
const typeColors = { 1: 'bg-blue-100 text-blue-700', 2: 'bg-green-100 text-green-700', 3: 'bg-orange-100 text-orange-700', 4: 'bg-slate-100 text-slate-700' };
const uomOptions = ['Each', 'Hour', 'Litre', 'Metre', 'Sq Metre', 'Kg', 'Day', 'Set', 'Roll', 'Pack'];

function ItemModal({ isOpen, onClose, onSubmit, loading, editItem }) {
  const defaultFormData = { 
    item_number: '', 
    name: '', 
    supplier: '', 
    item_type: 1, 
    uom: 'Each',
    cost: 0, 
    sell_price: 0, 
    sales_tax: 10, 
    purchase_tax: 10 
  };
  
  const [formData, setFormData] = useState(editItem || defaultFormData);
  
  React.useEffect(() => { 
    if (editItem) {
      setFormData({
        ...defaultFormData,
        ...editItem
      }); 
    } else {
      setFormData(defaultFormData); 
    }
  }, [editItem, isOpen]);
  
  const handleSubmit = (e) => { 
    e.preventDefault(); 
    if (!formData.name) { toast.error('Please enter name'); return; } 
    onSubmit(formData); 
  };

  // Auto-set UOM based on type
  const handleTypeChange = (newType) => {
    let suggestedUom = formData.uom;
    if (newType === 3) suggestedUom = 'Hour'; // Labour
    else if (newType === 1 && formData.uom === 'Hour') suggestedUom = 'Each'; // Item
    setFormData({ ...formData, item_type: parseInt(newType), uom: suggestedUom });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-card w-full max-w-lg">
        {/* Header */}
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-slate-700">{editItem ? 'Edit item' : 'Add new item'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
        </div>
        
        {/* Form - matching original system layout */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-5">
            {/* Item and services No. */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Item and services No.</label>
              <input 
                type="text" 
                value={formData.item_number} 
                onChange={(e) => setFormData({ ...formData, item_number: e.target.value })} 
                className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" 
                placeholder="e.g., WATTYL15L"
                data-testid="item-number"
              />
            </div>
            
            {/* Name */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Name</label>
              <input 
                type="text" 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" 
                data-testid="item-name"
              />
            </div>
            
            {/* Supplier */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Supplier</label>
              <input 
                type="text" 
                value={formData.supplier} 
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} 
                className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              />
            </div>
            
            {/* Type */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Type</label>
              <select 
                value={formData.item_type} 
                onChange={(e) => handleTypeChange(e.target.value)} 
                className="w-48 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-white"
              >
                <option value="">Select Type</option>
                {Object.entries(itemTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            
            {/* Unit of Measure */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Unit of measure</label>
              <select 
                value={formData.uom} 
                onChange={(e) => setFormData({ ...formData, uom: e.target.value })} 
                className="w-48 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-white"
              >
                {uomOptions.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            
            {/* Cost */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Cost</label>
              <div className="flex">
                <input 
                  type="number" 
                  value={formData.cost} 
                  onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })} 
                  className="w-36 px-3 py-2 border border-slate-300 rounded-l focus:ring-2 focus:ring-primary focus:border-primary outline-none" 
                  step="0.01"
                />
                <span className="px-4 py-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r text-slate-500 text-sm">aud</span>
              </div>
            </div>
            
            {/* Sell price */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Sell price</label>
              <div className="flex">
                <input 
                  type="number" 
                  value={formData.sell_price} 
                  onChange={(e) => setFormData({ ...formData, sell_price: parseFloat(e.target.value) || 0 })} 
                  className="w-36 px-3 py-2 border border-slate-300 rounded-l focus:ring-2 focus:ring-primary focus:border-primary outline-none" 
                  step="0.01"
                />
                <span className="px-4 py-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r text-slate-500 text-sm">aud</span>
              </div>
            </div>
            
            {/* Sales tax */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Sales tax</label>
              <div className="flex">
                <input 
                  type="number" 
                  value={formData.sales_tax} 
                  onChange={(e) => setFormData({ ...formData, sales_tax: parseFloat(e.target.value) || 0 })} 
                  className="w-36 px-3 py-2 border border-slate-300 rounded-l focus:ring-2 focus:ring-primary focus:border-primary outline-none" 
                  step="0.01"
                />
                <span className="px-4 py-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r text-slate-500 text-sm">%</span>
              </div>
            </div>
            
            {/* Purchase tax */}
            <div className="flex items-center">
              <label className="w-36 text-sm text-slate-500 flex-shrink-0">Purchase tax</label>
              <div className="flex">
                <input 
                  type="number" 
                  value={formData.purchase_tax} 
                  onChange={(e) => setFormData({ ...formData, purchase_tax: parseFloat(e.target.value) || 0 })} 
                  className="w-36 px-3 py-2 border border-slate-300 rounded-l focus:ring-2 focus:ring-primary focus:border-primary outline-none" 
                  step="0.01"
                />
                <span className="px-4 py-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r text-slate-500 text-sm">%</span>
              </div>
            </div>
          </div>
          
          {/* Footer buttons */}
          <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-5 py-2 border border-slate-300 rounded text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading} 
              className="px-5 py-2 bg-[#3498db] text-white rounded hover:bg-[#2980b9] flex items-center gap-2" 
              data-testid="save-item-btn"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {editItem ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ItemsServices() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const queryClient = useQueryClient();

  const { data: items, isLoading } = useQuery({ 
    queryKey: ['itemsServices', typeFilter], 
    queryFn: () => getItemsServices({ item_type: typeFilter || undefined }).then(res => res.data) 
  });
  
  const createMutation = useMutation({ 
    mutationFn: createItemService, 
    onSuccess: () => { 
      queryClient.invalidateQueries({ queryKey: ['itemsServices'] }); 
      setShowModal(false); 
      toast.success('Item created'); 
    } 
  });
  
  const updateMutation = useMutation({ 
    mutationFn: ({ id, data }) => updateItemService(id, data), 
    onSuccess: () => { 
      queryClient.invalidateQueries({ queryKey: ['itemsServices'] }); 
      setShowModal(false); 
      setEditItem(null); 
      toast.success('Item updated'); 
    } 
  });
  
  const deleteMutation = useMutation({ 
    mutationFn: deleteItemService, 
    onSuccess: () => { 
      queryClient.invalidateQueries({ queryKey: ['itemsServices'] }); 
      toast.success('Item deleted'); 
    } 
  });

  // Search by name, code, or supplier
  const filtered = items?.filter(i => 
    i.name?.toLowerCase().includes(search.toLowerCase()) ||
    i.item_number?.toLowerCase().includes(search.toLowerCase()) ||
    i.supplier?.toLowerCase().includes(search.toLowerCase())
  );
  
  const handleSubmit = (data) => { 
    if (editItem) updateMutation.mutate({ id: editItem.id, data }); 
    else createMutation.mutate(data); 
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="items-services-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Items and Services</h1>
          <p className="text-slate-500 mt-1">Manage catalog ({items?.length || 0} items)</p>
        </div>
        <button 
          onClick={() => { setEditItem(null); setShowModal(true); }} 
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-sm" 
          data-testid="add-item-btn"
        >
          <Plus size={20} />Add Item
        </button>
      </div>
      
      <div className="bg-white rounded-card border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              placeholder="Search by name, code, or supplier..." 
              className="w-full pl-10 pr-4 py-2 border rounded-none"
            />
          </div>
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)} 
            className="px-4 py-2 border rounded-none"
          >
            <option value="">All Types</option>
            {Object.entries(itemTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      
      <div className="bg-white rounded-card border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-semibold text-slate-600">Item Code</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Type</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Supplier</th>
                <th className="px-4 py-4 text-center text-sm font-semibold">UOM</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Cost</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Sell Price</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Loader2 className="animate-spin mx-auto text-primary" />
                  </td>
                </tr>
              ) : filtered?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Package size={48} className="mx-auto text-slate-300" />
                    <p className="mt-2 text-slate-500">No items found</p>
                  </td>
                </tr>
              ) : (
                filtered?.map((item) => (
                  <tr key={item.id} className="hover:bg-primary-light/30">
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono text-slate-500">{item.item_number || '-'}</span>
                    </td>
                    <td className="px-4 py-3 font-medium">{item.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${typeColors[item.item_type]}`}>
                        {itemTypes[item.item_type]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{item.supplier || '-'}</td>
                    <td className="px-4 py-3 text-center text-slate-600">{item.uom || 'Each'}</td>
                    <td className="px-4 py-3 text-right">${item.cost?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">${item.sell_price?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button 
                          onClick={() => { setEditItem(item); setShowModal(true); }} 
                          className="p-2 text-slate-500 hover:text-primary rounded-lg"
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => { if(window.confirm('Delete this item?')) deleteMutation.mutate(item.id); }} 
                          className="p-2 text-slate-500 hover:text-red-500 rounded-lg"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      <ItemModal 
        isOpen={showModal} 
        onClose={() => { setShowModal(false); setEditItem(null); }} 
        onSubmit={handleSubmit} 
        loading={createMutation.isPending || updateMutation.isPending} 
        editItem={editItem} 
      />
    </div>
  );
}

export default ItemsServices;
